// t121: # mentions of buildings, tasks, projects, journeys, issues and channels. Wire format: <#kind:uuid>.
import { useQuery } from '@tanstack/react-query';
import { ENTITY_TOKEN, entityToken, type MentionItem, type MentionKind } from '@tradeoutlet/shared';
import { api } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';

export const mentionKey = (ws: string, ref: string) => ['mention', ws, ref] as const;

/** "#Label" text in the composer back to <#kind:id> tokens (longest label first). */
export function encodeEntities(text: string, picked: Map<string, { kind: MentionKind; id: string }>) {
  let body = text;
  for (const [label, it] of [...picked.entries()].sort((a, b) => b[0].length - a[0].length)) {
    const shown = `#${label.replace(/^#/, '')}`;
    if (body.includes(shown)) body = body.split(shown).join(entityToken(it.kind, it.id));
  }
  return body;
}

/** Tokens back to "#Label" for editing; unknown items keep their token so nothing is lost. */
export function decodeEntities(body: string, labelOf: (kind: MentionKind, id: string) => string | null) {
  const picked = new Map<string, { kind: MentionKind; id: string }>();
  const text = body.replace(ENTITY_TOKEN, (tok, k: string, id: string) => {
    const kind = k.toLowerCase() as MentionKind;
    const label = labelOf(kind, id.toLowerCase());
    if (!label) return tok;
    const clean = label.replace(/^#/, '');
    picked.set(clean, { kind, id: id.toLowerCase() });
    return `#${clean}`;
  });
  return { text, picked };
}

/** Cached label for an item, if it's been resolved already (no fetch). */
export function cachedMention(ws: string | null | undefined, kind: MentionKind, id: string): MentionItem | null {
  return ws ? (queryClient.getQueryData<MentionItem | null>(mentionKey(ws, `${kind}:${id}`)) ?? null) : null;
}

// Batches every chip rendered in the same tick into one resolve call.
const pending = new Map<string, { refs: Set<string>; waiters: Map<string, ((v: MentionItem | null) => void)[]>; timer: ReturnType<typeof setTimeout> | null }>();
function resolveBatched(ws: string, ref: string): Promise<MentionItem | null> {
  let b = pending.get(ws);
  if (!b) { b = { refs: new Set(), waiters: new Map(), timer: null }; pending.set(ws, b); }
  const batch = b;
  batch.refs.add(ref);
  return new Promise((resolve, reject) => {
    batch.waiters.set(ref, [...(batch.waiters.get(ref) ?? []), resolve]);
    if (batch.timer) return;
    batch.timer = setTimeout(() => {
      pending.delete(ws);
      api.mentionResolve(ws, [...batch.refs])
        .then(r => { for (const [k, ws2] of batch.waiters) ws2.forEach(f => f(r.items[k] ?? null)); })
        .catch(reject);
    }, 15);
  });
}

export function useMentionItem(ws: string | null | undefined, kind: MentionKind, id: string) {
  return useQuery({ queryKey: mentionKey(ws ?? '', `${kind}:${id}`), queryFn: () => resolveBatched(ws!, `${kind}:${id}`), enabled: Boolean(ws), staleTime: 5 * 60_000 });
}

/** Seed the cache after picking an item in the composer, so the sent chip renders immediately. */
export const rememberMention = (ws: string, it: MentionItem) => queryClient.setQueryData(mentionKey(ws, `${it.kind}:${it.id}`), it);
